<template>
  <div class='centralized-issues-filters-header'>
    <div class="issue-search">
      <v-text-field
        :append-icon="search?'close':'search'"
        @click:append="clearSearch"
        @keyup.enter='submitSearchFilter'
        @keydown.native.escape="clearSearch"
        label="Search"
        solo
        v-model="search"
        class="issues-tab-search"
      >
        <template v-slot:append-outer>
          <div v-if='search' @mouseenter='searchMouseEnter' @click='submitSearchFilter' class='search-animation-wrapper'>
            <lottie-animation
              class='search-animation'
              :loop='true'
              :animationData="require('@/assets/icons/animations/search.json')"
              ref="searchAnimation"
              @loopComplete="loopComplete"
            />
          </div>
        </template>
      </v-text-field>
    </div>
    <v-switch
      class='filter-switch'
      v-model="hideSuppress"
      :label="$t('centralized.issues.hide.suppress.title')"
      @change='suppressChanged'
    ></v-switch>
    <v-switch
      class='filter-switch'
      v-model="displayOnlyCVEs"
      :label="$t('centralized.issues.hide.only.cves')"
      @change='onlyCVEsChanged'
    ></v-switch>
    <v-switch
      class='filter-switch'
      v-model="hideFixed"
      :label="$t('centralized.issues.hide.fixed.title')"
      @change='fixedChanged'
    ></v-switch>
  </div>
</template>

<script>

import LottieAnimation from 'lottie-web-vue';
import { mapGetters } from 'vuex';

export default {
  name: 'CentralizedIssuesFiltersHeader',
  components: {
    LottieAnimation,
  },
  props: {
    selectedDomains: {
      required: false,
      default: null,
      type: Array,
    },
    projectId: {
      required: false,
      default: null,
      type: String,
    },
  },
  created() {
    if (this.centralizedIssuesListOptions) {
      this.search = this.centralizedIssuesListOptions.search;
      this.specificSearch = this.centralizedIssuesListOptions.toggles.specificSearch;
      this.hideSuppress = this.centralizedIssuesListOptions.toggles.hideSuppress;
      this.displayOnlyCVEs = this.centralizedIssuesListOptions.toggles.onlyCVEs;
      this.hideFixed = this.centralizedIssuesListOptions.toggles.hideFixed;
    }
  },
  data() {
    return {
      search: '',
      hideSuppress: true,
      displayOnlyCVEs: false,
      hideFixed: true,
      specificSearch: false,
    };
  },
  computed: {
    ...mapGetters(['centralizedIssuesListOptions']),
    basicFilterForLoadingIssues() {
      const payload = {};
      if (this.selectedDomains) {
        payload.domain = this.selectedDomains;
      }
      if (this.projectId) {
        payload.projectId = this.projectId;
      }
      return payload;
    },
  },
  watch: {
    search() {
      this.$emit('resetSpecificSearch');
    },
    'centralizedIssuesListOptions.search': {
      handler() {
        this.search = this.centralizedIssuesListOptions.search;
      },
      deep: true,
    },
  },
  methods: {
    searchMouseEnter() {
      this.$refs.searchAnimation.play();
    },
    loopComplete() {
      this.$refs.searchAnimation.pause();
    },
    async suppressChanged() {
      const payload = {
        ...this.basicFilterForLoadingIssues,
        hideSuppress: this.hideSuppress,
        page: 1,
      };
      await this.loadIssues(payload);
    },
    async onlyCVEsChanged() {
      const payload = {
        ...this.basicFilterForLoadingIssues,
        onlyCVEs: this.displayOnlyCVEs,
        page: 1,
      };
      await this.loadIssues(payload);
    },
    async fixedChanged() {
      const payload = {
        ...this.basicFilterForLoadingIssues,
        hideFixed: this.hideFixed,
        page: 1,
      };
      await this.loadIssues(payload);
    },
    async submitSearchFilter() {
      this.specificSearch = false;
      if (this.search.length) {
        const payload = {
          ...this.basicFilterForLoadingIssues,
          specificSearch: this.specificSearch,
          search: this.search.trim(),
          searchUpdated: false,
          page: 1,
        };
        await this.loadIssues(payload);
      }
    },
    async clearSearch() {
      this.search = '';
      this.specificSearch = false;
      const payload = {
        ...this.basicFilterForLoadingIssues,
        specificSearch: this.specificSearch,
        search: this.search,
        searchUpdated: true,
        page: 1,
      };
      await this.loadIssues(payload);
    },
    async loadIssues(payload) {
      await this.$emit('loadIssues', payload);
    },
  },
};
</script>

<style lang='scss' scoped>
.centralized-issues-filters-header {
  display: flex;
  flex-direction: row;
  align-items: center;
  margin-bottom: 15px;
  .issue-search {
    min-width: 400px;
    margin-left: 15px;
  }
  .filter-switch {
    margin-left: 40px;
    ::v-deep .v-input--selection-controls {
      margin-top: 0;
      padding-top: 0;
    }
    ::v-deep label {
      color: var(--text-secondary) !important;
    }
  }
  .issues-tab-search {
    ::v-deep .v-input__icon--append .v-icon {
      font-size: 18px !important;
    }
    ::v-deep .v-input--is-focused .v-input__icon--append .v-icon,
    ::v-deep .v-input--is-dirty .v-input__icon--append .v-icon {
      color: var(--brand-primary) !important;
    }
  }
  .search-animation-wrapper {
    width: 24px;
    height: 24px;
    display: flex;
    align-items: center;
    justify-content: center;
    overflow: hidden;
    cursor: pointer;
    color: var(--text-secondary);
    .search-animation {
      width: 24px !important;
      height: 24px !important;
      max-width: 24px !important;
      max-height: 24px !important;
    }
    ::v-deep div {
      width: 24px !important;
      height: 24px !important;
    }
    ::v-deep svg {
      width: 24px !important;
      height: 24px !important;
    }
    // Filled paths: use currentColor for fill, suppress any stroke
    ::v-deep svg path, ::v-deep svg ellipse {
      fill: currentColor !important;
      stroke: none !important;
    }
    // Stroke-based paths (have stroke attribute set by Lottie): restore stroke as currentColor
    ::v-deep svg path[stroke], ::v-deep svg ellipse[stroke] {
      fill: none !important;
      stroke: currentColor !important;
    }
  }
}
</style>
